import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  X,
  BadgeCheck,
  Briefcase,
  Building,
  Scale,
  Calculator,
  TrendingUp,
} from 'lucide-react';

const PARTNER_TYPES = [
  { id: 'cbv', dbValue: 'cbv', title: 'Chartered Business Valuator (CBV)', description: 'Professional business valuation services', icon: BadgeCheck },
  { id: 'broker', dbValue: 'business_broker', title: 'Business Broker', description: 'Facilitate business sales and acquisitions', icon: Briefcase },
  { id: 'lender', dbValue: 'lender', title: 'Commercial Lender', description: 'Provide financing for business transactions', icon: Building },
  { id: 'lawyer', dbValue: 'lawyer', title: 'Business Lawyer', description: 'Legal counsel for business transactions', icon: Scale },
  { id: 'accountant', dbValue: 'accountant', title: 'Accountant / CPA', description: 'Tax and financial advisory services', icon: Calculator },
  { id: 'cepa', dbValue: 'cepa_coach', title: 'CEPA-Certified Exit Planner', description: 'Certified exit planning professional', icon: TrendingUp },
];

const PROVINCES = [
  'British Columbia', 'Alberta', 'Saskatchewan', 'Manitoba',
  'Ontario', 'Quebec', 'Atlantic', 'Canada-wide',
];

const INDUSTRIES = [
  'Manufacturing', 'Technology', 'Healthcare', 'Retail',
  'Professional Services', 'Construction', 'Food & Hospitality',
  'Transportation', 'Financial Services', 'Real Estate',
  'Agriculture', 'SaaS', 'E-Commerce',
];

const TOTAL_STEPS = 5;
const DRAFT_KEY = 'partner_onboarding_draft';

const COMPLETE_PARTNER_ONBOARDING = gql`
  mutation CompletePartnerOnboarding($bio: String, $acceptsIntroductions: Boolean) {
    complete_partner_onboarding(bio: $bio, accepts_introductions: $acceptsIntroductions) {
      partner_profile {
        id
        name
        partner_category
      }
      errors
    }
  }
`;

const CREATE_PARTNER_PROFILE = gql`
  mutation CreatePartnerProfile(
    $partnerCategory: String!
    $name: String
    $orgName: String
    $contactEmail: String
    $phone: String
    $websiteUrl: String
    $geographyRegions: [String!]
    $industryTags: [String!]
    $bio: String
    $acceptsIntroductions: Boolean
  ) {
    create_partner_profile(
      partner_category: $partnerCategory
      name: $name
      org_name: $orgName
      contact_email: $contactEmail
      phone: $phone
      website_url: $websiteUrl
      geography_regions: $geographyRegions
      industry_tags: $industryTags
      bio: $bio
      accepts_introductions: $acceptsIntroductions
    ) {
      partner_profile {
        id
        name
        partner_category
        per_lead_fee_cents
      }
      errors
    }
  }
`;

const GET_FEE_SCHEDULE = gql`
  query CategoryFeeSchedule {
    category_fee_schedule
  }
`;

const INITIAL_FORM = {
  partnerType: '',
  fullName: '',
  firmName: '',
  email: '',
  phone: '',
  website: '',
  regions: [],
  industries: [],
  acceptingIntros: true,
  bio: '',
};

// Invite-accept variant: a single substantive prompt (one-line bio + listing
// opt-in) before the deal-team workspace lands. The full self-signup wizard
// below is unchanged. CoachProfile fields (name, email, firm, partner category)
// are pre-seeded by the controller during invite-accept and surfaced here as
// read-only context so the invitee knows what was captured.
function InviteAcceptOnboarding({ returnTo, prefilled }) {
  const safePrefilled = prefilled || {};
  const [bio, setBio] = useState('');
  const [acceptingIntros, setAcceptingIntros] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [completeOnboarding, { loading: submitting }] = useMutation(COMPLETE_PARTNER_ONBOARDING);

  const finish = (trackEvent) => {
    if (typeof window.ahoy !== 'undefined') {
      window.ahoy.track(trackEvent, { mode: 'invite_accept' });
    }
    window.location.href = returnTo || '/succeedo/home';
  };

  const handleSubmit = async () => {
    setSubmitErrors([]);
    try {
      const { data } = await completeOnboarding({
        variables: {
          bio: bio || null,
          acceptsIntroductions: acceptingIntros,
        },
      });
      const errors = data?.complete_partner_onboarding?.errors || [];
      if (errors.length > 0) {
        setSubmitErrors(errors);
        return;
      }
      finish('succeedo_partner_onboarding_invite_accept_submitted');
    } catch (e) {
      setSubmitErrors(['Something went wrong. Please try again.']);
    }
  };

  const handleSkip = async () => {
    // "Skip" still flips needs_onboarding: false so the user isn't bounced
    // back into the wizard on next page load. The full profile is filled in
    // later from the partner-dashboard "complete your profile" banner.
    setSubmitErrors([]);
    try {
      await completeOnboarding({ variables: { bio: null, acceptsIntroductions: null } });
      finish('succeedo_partner_onboarding_invite_accept_skipped');
    } catch (e) {
      // Failure here is non-blocking — push the user through to the workspace
      // anyway; they'll see the wizard again next time, which is fine.
      finish('succeedo_partner_onboarding_invite_accept_skipped');
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--background))] to-[hsl(var(--muted))]/20 backdrop-blur-3xl" />

      <div className="relative bg-white dark:bg-white rounded-[16px] p-8 max-w-[600px] w-full shadow-2xl">
        <div className="font-mono text-[11px] uppercase text-[#a3a3a3] mb-3 tracking-wide">
          QUICK PROFILE QUESTION
        </div>
        <h1 className="font-serif text-[32px] font-light text-[#0a0a0a] tracking-[-0.02em] mb-6">
          Welcome to your deal team.
        </h1>
        <p className="text-[15px] text-[#525252] mb-6">
          We pre-filled your profile from the invite. One quick question before you head
          to the workspace.
        </p>

        {/* Pre-filled context (read-only) */}
        {(safePrefilled.name || safePrefilled.email || safePrefilled.orgName) && (
          <div className="bg-[#f5f5f5] border border-[#e5e5e5] rounded-[12px] p-4 mb-6 space-y-1">
            {safePrefilled.name && (
              <div className="text-[14px] text-[#0a0a0a]"><span className="text-[#737373]">Name: </span>{safePrefilled.name}</div>
            )}
            {safePrefilled.orgName && (
              <div className="text-[14px] text-[#0a0a0a]"><span className="text-[#737373]">Firm: </span>{safePrefilled.orgName}</div>
            )}
            {safePrefilled.email && (
              <div className="text-[14px] text-[#0a0a0a]"><span className="text-[#737373]">Email: </span>{safePrefilled.email}</div>
            )}
          </div>
        )}

        {submitErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-[12px] p-4 mb-6">
            {submitErrors.map((err) => (
              <div key={err} className="text-[13px] text-red-600">{err}</div>
            ))}
          </div>
        )}

        {/* The single substantive prompt */}
        <div className="mb-6">
          <label className="block text-[13px] text-[#737373] mb-2">
            Tell sellers what you do <span className="text-[#a3a3a3]">(one line)</span>
          </label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="e.g. I value Canadian SMBs across professional services and manufacturing."
            rows={3}
            maxLength={280}
            className="w-full bg-white border border-[#e5e5e5] rounded-[8px] px-4 py-3 text-[#0a0a0a] placeholder:text-[#a3a3a3] focus:border-[#f76b15] focus:outline-none transition-colors resize-none"
          />
        </div>

        {/* Listing opt-in (default off) */}
        <div className="flex items-start justify-between mb-8 gap-4">
          <div>
            <div className="text-[15px] font-medium text-[#0a0a0a] mb-1">
              List me in the partner directory
            </div>
            <div className="text-[13px] text-[#737373]">
              Other sellers can request introductions. You can change this later.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setAcceptingIntros((v) => !v)}
            className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 mt-1 ${
              acceptingIntros ? 'bg-[#f76b15]' : 'bg-[#e5e5e5]'
            }`}
            aria-label="Toggle marketplace listing"
          >
            <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
              acceptingIntros ? 'translate-x-6' : 'translate-x-0.5'
            }`} />
          </button>
        </div>

        <div className="flex gap-3 items-center">
          <button
            type="button"
            onClick={handleSkip}
            disabled={submitting}
            className="text-[14px] text-[#737373] hover:text-[#0a0a0a] transition-colors px-4 disabled:opacity-50"
          >
            Skip for now
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 bg-[#0a0a0a] text-white rounded-full px-6 py-3 font-medium transition-all disabled:bg-[#a3a3a3] disabled:cursor-not-allowed hover:bg-[#262626]"
          >
            {submitting ? 'Saving...' : 'Continue to workspace →'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PartnerOnboardingWizard({ mode, returnTo, prefilled }) {
  // Invite-accept variant short-circuits the 5-step self-signup wizard.
  // Pre-seeded values + return-to URL are handed in from the Rails controller.
  if (mode === 'invite_accept') {
    return (
      <InviteAcceptOnboarding
        returnTo={returnTo || null}
        prefilled={prefilled || {}}
      />
    );
  }

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);

  const [createProfile, { loading: submitting }] = useMutation(CREATE_PARTNER_PROFILE);
  const { data: feeData } = useQuery(GET_FEE_SCHEDULE);

  const feeSchedule = feeData?.category_fee_schedule || {};

  // Load draft from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setFormData((prev) => ({ ...prev, ...parsed }));
      }
    } catch (_) { /* ignore */ }
    // Track wizard start
    if (typeof window.ahoy !== 'undefined') {
      window.ahoy.track('succeedo_partner_onboarding_started');
    }
  }, []);

  // Persist draft to localStorage
  useEffect(() => {
    if (!submitted) {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
    }
  }, [formData, submitted]);

  const update = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const toggleArrayItem = (field, item) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter((i) => i !== item)
        : [...prev[field], item],
    }));
  };

  const canContinue = () => {
    switch (step) {
      case 1: return formData.partnerType !== '';
      case 2: return formData.fullName && formData.email;
      case 3: return formData.regions.length > 0 && formData.industries.length > 0;
      case 4: return true;
      default: return false;
    }
  };

  const handleContinue = () => {
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
      if (typeof window.ahoy !== 'undefined') {
        window.ahoy.track('succeedo_partner_onboarding_step_completed', { step });
      }
    }
  };

  const handleBack = () => { if (step > 1) setStep(step - 1); };

  const handleSubmit = async () => {
    setSubmitErrors([]);
    const partnerType = PARTNER_TYPES.find((t) => t.id === formData.partnerType);
    try {
      const { data } = await createProfile({
        variables: {
          partnerCategory: partnerType?.dbValue || formData.partnerType,
          name: formData.fullName,
          orgName: formData.firmName,
          contactEmail: formData.email,
          phone: formData.phone || null,
          websiteUrl: formData.website || null,
          geographyRegions: formData.regions,
          industryTags: formData.industries,
          bio: formData.bio || null,
          acceptsIntroductions: formData.acceptingIntros,
        },
      });
      const errors = data?.create_partner_profile?.errors || [];
      if (errors.length > 0) {
        setSubmitErrors(errors);
        return;
      }
      // Success
      localStorage.removeItem(DRAFT_KEY);
      setSubmitted(true);
      if (typeof window.ahoy !== 'undefined') {
        window.ahoy.track('succeedo_partner_onboarding_submitted', {
          partner_category: partnerType?.dbValue,
        });
      }
      setTimeout(() => { window.location.href = '/succeedo/home'; }, 2000);
    } catch (e) {
      setSubmitErrors(['Something went wrong. Please try again.']);
    }
  };

  // Resolve the fee for the selected category
  const selectedType = PARTNER_TYPES.find((t) => t.id === formData.partnerType);
  const categoryFee = selectedType ? feeSchedule[selectedType.dbValue] : null;
  const feeDisplay = categoryFee ? `$${(categoryFee / 100).toLocaleString()}` : null;

  if (submitted) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-6">
        <div className="bg-white dark:bg-white rounded-[16px] p-8 max-w-[600px] w-full shadow-2xl text-center">
          <div className="w-16 h-16 bg-[#f76b15]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <BadgeCheck className="w-8 h-8 text-[#f76b15]" />
          </div>
          <h1 className="font-serif text-[32px] font-light text-[#0a0a0a] mb-3">
            Welcome to Succeedo
          </h1>
          <p className="text-[16px] text-[#737373]">
            Your partner profile has been created. Redirecting to your dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--background))] to-[hsl(var(--muted))]/20 backdrop-blur-3xl" />

      <div className="relative bg-white dark:bg-white rounded-[16px] p-8 max-w-[600px] w-full shadow-2xl">
        {/* Close button */}
        <a href="/succeedo/home" className="absolute top-6 right-6 text-[#a3a3a3] hover:text-[#0a0a0a] transition-colors">
          <X className="w-5 h-5" />
        </a>

        {/* Step Indicator */}
        <div className="mb-8">
          <div className="font-mono text-[11px] uppercase text-[#a3a3a3] mb-4 tracking-wide">
            STEP {step} OF {TOTAL_STEPS}
          </div>
          <div className="flex gap-2">
            {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
              <div
                key={index}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  index < step
                    ? 'bg-[#f76b15]'
                    : 'border border-[#e5e5e5] bg-transparent'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Step 1: Partner Type */}
        {step === 1 && (
          <div>
            <h1 className="font-serif text-[32px] font-light text-[#0a0a0a] tracking-[-0.02em] mb-8">
              What type of advisor are you?
            </h1>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {PARTNER_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = formData.partnerType === type.id;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => update('partnerType', type.id)}
                    className={`rounded-[16px] p-5 text-left transition-all ${
                      isSelected
                        ? 'bg-[#f76b15]/5 border-2 border-[#f76b15]'
                        : 'bg-[#f5f5f5] border border-[#e5e5e5] hover:border-[#a3a3a3]'
                    }`}
                  >
                    <Icon className="w-6 h-6 text-[#0a0a0a] mb-3" strokeWidth={2} />
                    <div className="text-[14px] font-medium text-[#0a0a0a] mb-1">{type.title}</div>
                    <div className="text-[12px] text-[#737373]">{type.description}</div>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={handleContinue}
              disabled={!canContinue()}
              className="w-full bg-[#0a0a0a] text-white rounded-full px-6 py-3 font-medium transition-all disabled:bg-[#a3a3a3] disabled:cursor-not-allowed hover:bg-[#262626]"
            >
              Continue &rarr;
            </button>
          </div>
        )}

        {/* Step 2: Basic Info */}
        {step === 2 && (
          <div>
            <h1 className="font-serif text-[32px] font-light text-[#0a0a0a] tracking-[-0.02em] mb-8">
              Tell us about yourself
            </h1>

            <div className="space-y-5 mb-8">
              <div>
                <label className="block text-[13px] text-[#737373] mb-2">Full Name</label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => update('fullName', e.target.value)}
                  placeholder="John Smith"
                  className="w-full bg-white border border-[#e5e5e5] rounded-[8px] px-4 py-3 text-[#0a0a0a] placeholder:text-[#a3a3a3] focus:border-[#f76b15] focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-[13px] text-[#737373] mb-2">Firm / Company Name</label>
                <input
                  type="text"
                  value={formData.firmName}
                  onChange={(e) => update('firmName', e.target.value)}
                  placeholder="Smith & Associates"
                  className="w-full bg-white border border-[#e5e5e5] rounded-[8px] px-4 py-3 text-[#0a0a0a] placeholder:text-[#a3a3a3] focus:border-[#f76b15] focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-[13px] text-[#737373] mb-2">Professional Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder="john@smithassociates.com"
                  className="w-full bg-white border border-[#e5e5e5] rounded-[8px] px-4 py-3 text-[#0a0a0a] placeholder:text-[#a3a3a3] focus:border-[#f76b15] focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-[13px] text-[#737373] mb-2">Phone (optional)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="w-full bg-white border border-[#e5e5e5] rounded-[8px] px-4 py-3 text-[#0a0a0a] placeholder:text-[#a3a3a3] focus:border-[#f76b15] focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-[13px] text-[#737373] mb-2">Website URL</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => update('website', e.target.value)}
                  placeholder="https://smithassociates.com"
                  className="w-full bg-white border border-[#e5e5e5] rounded-[8px] px-4 py-3 text-[#0a0a0a] placeholder:text-[#a3a3a3] focus:border-[#f76b15] focus:outline-none transition-colors"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={handleBack} className="text-[14px] text-[#737373] hover:text-[#0a0a0a] transition-colors px-4">
                &larr; Back
              </button>
              <button
                type="button"
                onClick={handleContinue}
                disabled={!canContinue()}
                className="flex-1 bg-[#0a0a0a] text-white rounded-full px-6 py-3 font-medium transition-all disabled:bg-[#a3a3a3] disabled:cursor-not-allowed hover:bg-[#262626]"
              >
                Continue &rarr;
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Geography & Industries */}
        {step === 3 && (
          <div>
            <h1 className="font-serif text-[32px] font-light text-[#0a0a0a] tracking-[-0.02em] mb-8">
              Where do you work and who do you serve?
            </h1>

            <div className="space-y-6 mb-8">
              <div>
                <div className="font-mono text-[11px] uppercase text-[#a3a3a3] mb-3 tracking-wide">REGIONS</div>
                <div className="flex flex-wrap gap-2">
                  {PROVINCES.map((region) => (
                    <button
                      key={region}
                      type="button"
                      onClick={() => toggleArrayItem('regions', region)}
                      className={`rounded-full px-4 py-2 text-[13px] border transition-all ${
                        formData.regions.includes(region)
                          ? 'bg-[#0a0a0a] text-white border-[#0a0a0a]'
                          : 'bg-[#fafafa] text-[#0a0a0a] border-[#e5e5e5] hover:border-[#a3a3a3]'
                      }`}
                    >
                      {region}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="font-mono text-[11px] uppercase text-[#a3a3a3] mb-3 tracking-wide">INDUSTRIES</div>
                <div className="flex flex-wrap gap-2">
                  {INDUSTRIES.map((industry) => (
                    <button
                      key={industry}
                      type="button"
                      onClick={() => toggleArrayItem('industries', industry)}
                      className={`rounded-full px-4 py-2 text-[13px] border transition-all ${
                        formData.industries.includes(industry)
                          ? 'bg-[#0a0a0a] text-white border-[#0a0a0a]'
                          : 'bg-[#fafafa] text-[#0a0a0a] border-[#e5e5e5] hover:border-[#a3a3a3]'
                      }`}
                    >
                      {industry}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={handleBack} className="text-[14px] text-[#737373] hover:text-[#0a0a0a] transition-colors px-4">
                &larr; Back
              </button>
              <button
                type="button"
                onClick={handleContinue}
                disabled={!canContinue()}
                className="flex-1 bg-[#0a0a0a] text-white rounded-full px-6 py-3 font-medium transition-all disabled:bg-[#a3a3a3] disabled:cursor-not-allowed hover:bg-[#262626]"
              >
                Continue &rarr;
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Introduction Preferences */}
        {step === 4 && (
          <div>
            <h1 className="font-serif text-[32px] font-light text-[#0a0a0a] tracking-[-0.02em] mb-8">
              How would you like to receive introductions?
            </h1>

            <div className="space-y-6 mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[15px] font-medium text-[#0a0a0a] mb-1">Accepting Introductions</div>
                  <div className="text-[13px] text-[#737373]">Allow Succeedo to connect you with potential clients</div>
                </div>
                <button
                  type="button"
                  onClick={() => update('acceptingIntros', !formData.acceptingIntros)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    formData.acceptingIntros ? 'bg-[#f76b15]' : 'bg-[#e5e5e5]'
                  }`}
                >
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                    formData.acceptingIntros ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              {formData.acceptingIntros && (
                <>
                  {/* Platform fee display (read-only) */}
                  {feeDisplay && (
                    <div className="bg-[#f5f5f5] border border-[#e5e5e5] rounded-[12px] p-4">
                      <div className="font-mono text-[11px] uppercase text-[#a3a3a3] mb-2 tracking-wide">
                        PER-INTRODUCTION FEE
                      </div>
                      <div className="text-[24px] font-light text-[#0a0a0a]">
                        {feeDisplay}
                        <span className="text-[14px] text-[#737373] ml-2">per introduction</span>
                      </div>
                      <div className="text-[12px] text-[#a3a3a3] mt-1">
                        Platform-set rate for {selectedType?.title || 'your category'}. Charged only when an introduction is accepted.
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-[13px] text-[#737373] mb-2">Brief Introduction (optional)</label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => update('bio', e.target.value)}
                      placeholder="Tell potential clients about your expertise and approach..."
                      rows={4}
                      className="w-full bg-white border border-[#e5e5e5] rounded-[8px] px-4 py-3 text-[#0a0a0a] placeholder:text-[#a3a3a3] focus:border-[#f76b15] focus:outline-none transition-colors resize-none"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={handleBack} className="text-[14px] text-[#737373] hover:text-[#0a0a0a] transition-colors px-4">
                &larr; Back
              </button>
              <button
                type="button"
                onClick={handleContinue}
                className="flex-1 bg-[#0a0a0a] text-white rounded-full px-6 py-3 font-medium transition-all hover:bg-[#262626]"
              >
                Continue &rarr;
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Review & Submit */}
        {step === 5 && (
          <div>
            <h1 className="font-serif text-[32px] font-light text-[#0a0a0a] tracking-[-0.02em] mb-8">
              Review &amp; Submit
            </h1>

            {submitErrors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-[12px] p-4 mb-6">
                {submitErrors.map((err, i) => (
                  <div key={i} className="text-[13px] text-red-600">{err}</div>
                ))}
              </div>
            )}

            <div className="bg-[#f5f5f5] border border-[#e5e5e5] rounded-[16px] p-6 space-y-6 mb-8">
              {/* Partner Type */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[11px] uppercase text-[#a3a3a3] tracking-wide">PARTNER TYPE</div>
                  <button type="button" onClick={() => setStep(1)} className="text-[13px] text-[#737373] hover:text-[#0a0a0a] transition-colors">Edit</button>
                </div>
                <div className="text-[15px] text-[#0a0a0a]">
                  {PARTNER_TYPES.find((t) => t.id === formData.partnerType)?.title || 'Not selected'}
                </div>
              </div>

              <div className="h-px bg-[#e5e5e5]" />

              {/* Contact Info */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[11px] uppercase text-[#a3a3a3] tracking-wide">CONTACT INFORMATION</div>
                  <button type="button" onClick={() => setStep(2)} className="text-[13px] text-[#737373] hover:text-[#0a0a0a] transition-colors">Edit</button>
                </div>
                <div className="space-y-1">
                  <div className="text-[15px] text-[#0a0a0a]">{formData.fullName}</div>
                  {formData.firmName && <div className="text-[15px] text-[#0a0a0a]">{formData.firmName}</div>}
                  <div className="text-[15px] text-[#0a0a0a]">{formData.email}</div>
                  {formData.phone && <div className="text-[15px] text-[#0a0a0a]">{formData.phone}</div>}
                  {formData.website && <div className="text-[15px] text-[#0a0a0a]">{formData.website}</div>}
                </div>
              </div>

              <div className="h-px bg-[#e5e5e5]" />

              {/* Regions & Industries */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[11px] uppercase text-[#a3a3a3] tracking-wide">REGIONS &amp; INDUSTRIES</div>
                  <button type="button" onClick={() => setStep(3)} className="text-[13px] text-[#737373] hover:text-[#0a0a0a] transition-colors">Edit</button>
                </div>
                <div className="space-y-2">
                  <div className="text-[15px] text-[#0a0a0a]">{formData.regions.join(', ')}</div>
                  <div className="text-[15px] text-[#0a0a0a]">{formData.industries.join(', ')}</div>
                </div>
              </div>

              <div className="h-px bg-[#e5e5e5]" />

              {/* Preferences */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[11px] uppercase text-[#a3a3a3] tracking-wide">INTRODUCTION PREFERENCES</div>
                  <button type="button" onClick={() => setStep(4)} className="text-[13px] text-[#737373] hover:text-[#0a0a0a] transition-colors">Edit</button>
                </div>
                <div className="space-y-1">
                  <div className="text-[15px] text-[#0a0a0a]">
                    {formData.acceptingIntros ? 'Accepting introductions' : 'Not accepting introductions'}
                  </div>
                  {feeDisplay && (
                    <div className="text-[15px] text-[#0a0a0a]">
                      Per-introduction fee: {feeDisplay}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={handleBack} className="text-[14px] text-[#737373] hover:text-[#0a0a0a] transition-colors px-4">
                &larr; Back
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 bg-[#0a0a0a] text-white rounded-full px-6 py-3 text-[16px] font-medium text-center hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {submitting ? 'Creating Profile...' : 'Create My Partner Profile'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

InviteAcceptOnboarding.propTypes = {
  returnTo: PropTypes.string,
  prefilled: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    orgName: PropTypes.string,
    partnerCategory: PropTypes.string,
  }),
};

InviteAcceptOnboarding.defaultProps = {
  returnTo: null,
  prefilled: {},
};

PartnerOnboardingWizard.propTypes = {
  mode: PropTypes.oneOf(['invite_accept', 'self_signup']),
  returnTo: PropTypes.string,
  prefilled: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    orgName: PropTypes.string,
    partnerCategory: PropTypes.string,
  }),
};

PartnerOnboardingWizard.defaultProps = {
  mode: 'self_signup',
  returnTo: null,
  prefilled: {},
};
